import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  /** Auto-reload after this many ms (e.g. 10000 for kiosk per CLAUDE.md §24). */
  autoReloadAfterMs?: number;
  fallback?: (error: Error) => ReactNode;
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private reloadTimer: ReturnType<typeof setTimeout> | null = null;

  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Log error category, never PII (CLAUDE.md §18.7).
    console.error('[ErrorBoundary]', error.name, info.componentStack);
  }

  override componentDidUpdate(_prev: ErrorBoundaryProps, prevState: ErrorBoundaryState): void {
    if (this.state.error && !prevState.error && this.props.autoReloadAfterMs) {
      this.reloadTimer = setTimeout(() => {
        if (typeof window !== 'undefined') window.location.reload();
      }, this.props.autoReloadAfterMs);
    }
  }

  override componentWillUnmount(): void {
    if (this.reloadTimer) clearTimeout(this.reloadTimer);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) return this.props.fallback(error);

    return (
      <div role="alert" className="flex flex-col items-center gap-4 p-8 text-center">
        <h2 className="text-xl font-semibold">Ein Fehler ist aufgetreten</h2>
        <p className="text-muted-foreground">{error.message}</p>
        {this.props.autoReloadAfterMs ? (
          <p className="text-sm text-muted-foreground">
            Die Seite wird in {Math.round(this.props.autoReloadAfterMs / 1000)} s neu geladen.
          </p>
        ) : (
          <button
            type="button"
            className="rounded-md bg-primary px-4 py-2 text-primary-foreground"
            onClick={() => window.location.reload()}
          >
            Neu laden
          </button>
        )}
      </div>
    );
  }
}
